import { useEffect, useState } from "react";
import { WebRoutes } from "consts/enpoints/web";
import { KYCProviders } from "consts/kyc";
import { createPspKycSetting } from "features/pspKycSettings/api";
import { resetPspKycSettings } from "features/pspKycSettings/slice";
import type {
    PspKycSetting,
    PspKycSettingCreatePayload
} from "features/pspKycSettings/types";
import usePaymentMethodsActions from "features/paymentMethods/useActions";
import { useAppDispatch } from "hooks/store";
import { useNavigate } from "infrastructure/router";

type FieldErrors = Partial<Record<keyof PspKycSettingCreatePayload, string>>;

type ResponseEnvelope = {
    success?: boolean;
    code?: number;
    message?: string;
    data?: unknown;
    errors?: Record<string, Array<string>>;
};

const UNSET_KYC_TYPE_ID = -1;

const initialValues: PspKycSettingCreatePayload = {
    MOR: '',
    methodId: 0,
    PSP: '',
    countryIso2: '',
    kycProvider: '',
    kycTypeId: UNSET_KYC_TYPE_ID
};

const isErrorEnvelope = (json: ResponseEnvelope) =>
    json?.success === false || (typeof json?.code === 'number' && json.code >= 400);

const extractFieldErrors = (json: ResponseEnvelope): FieldErrors => {
    const source = json?.errors
        ?? (json?.data && typeof json.data === 'object' && !Array.isArray(json.data)
            ? json.data as Record<string, Array<string>>
            : null);

    if (!source) return {};

    const mapped: FieldErrors = {};
    Object.entries(source).forEach(([key, messages]) => {
        if (Array.isArray(messages) && messages.length > 0) {
            mapped[key as keyof PspKycSettingCreatePayload] = messages[0];
        }
    });
    return mapped;
};

export default function useKycSettingForm() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { getAllPaymentMethods } = usePaymentMethodsActions();

    const [formValues, setFormValues] = useState<PspKycSettingCreatePayload>(initialValues);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        getAllPaymentMethods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFieldChange = <K extends keyof PspKycSettingCreatePayload>(
        name: K,
        value: PspKycSettingCreatePayload[K]
    ) => {
        setFormValues(prev => ({ ...prev, [name]: value }));
        setFieldErrors(prev => {
            if (!prev[name]) return prev;
            const next = { ...prev };
            delete next[name];
            return next;
        });
    };

    const validate = (values: PspKycSettingCreatePayload): FieldErrors => {
        const errors: FieldErrors = {};

        if (!values.PSP) {
            errors.PSP = 'PSP Provider is required';
        }
        if (!values.kycProvider) {
            errors.kycProvider = 'KYC Provider is required';
        }
        if (values.kycProvider === KYCProviders.Manual) {
            errors.kycProvider = 'Invalid KYC Provider';
        }
        if (
            typeof values.kycTypeId !== 'number'
            || Number.isNaN(values.kycTypeId)
            || values.kycTypeId < 0
        ) {
            errors.kycTypeId = 'KYC Type is required';
        }
        if (values.countryIso2 === undefined || values.countryIso2 === null) {
            errors.countryIso2 = 'Country is required';
        }
        if (values.methodId < 0) {
            errors.methodId = 'Invalid Payment Method';
        }

        return errors;
    };

    const handleSubmit = async () => {
        const errors = validate(formValues);
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const response = await createPspKycSetting(formValues);
            const json: ResponseEnvelope = await response.json();

            if (!response.ok || isErrorEnvelope(json)) {
                const beErrors = extractFieldErrors(json);
                if (Object.keys(beErrors).length > 0) {
                    setFieldErrors(beErrors);
                }
                throw new Error(json?.message ?? `Request failed with status ${response.status}`);
            }

            dispatch(resetPspKycSettings());

            const data = json?.data as PspKycSetting | undefined;
            const newCoreId = data?.coreId;
            if (newCoreId !== undefined && newCoreId !== null) {
                navigate(WebRoutes.KYCSettings.PSPKYCSettings.Setting(newCoreId));
            } else {
                navigate(WebRoutes.KYCSettings.PSPKYCSettings.Index);
            }
        } catch (err: any) {
            setError(err?.message ?? 'Failed to create PSP KYC setting');
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        formValues,
        fieldErrors,
        error,
        isSubmitting,
        onFieldChange,
        handleSubmit
    };
}
